import React, { useState, useEffect } from "react";
import { Tabs, Card, Alert, Badge, Button, Drawer, notification } from "antd";
import { RobotOutlined } from "@ant-design/icons";
import FlightListView from "./components/FlightListView";
import FlightCalendarView from "./components/FlightCalendarView";
import FlightTimelineView from "./components/FlightTimelineView";
import TransactionModal from "./components/TransactionModal";
import AgentActionsPanel from "./components/AgentActionsPanel";
import { mockFlightOperations } from "./mockData"; // Import mock data

const { TabPane } = Tabs;

/**
 * 航班监控 - 示例页面
 * 该页面用于展示航班监控的核心功能和交互逻辑
 * 包含：列表视图、日历视图、时间轴视图、事务处理和AI助手功能
 */
const FlightMonitorDemoPage = () => {
    // 状态管理
    const [activeView, setActiveView] = useState("list"); // 'list', 'calendar', 'timeline'
    const [flights, setFlights] = useState(mockFlightOperations);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [agentPanelVisible, setAgentPanelVisible] = useState(false);
    const [pendingAgentActions, setPendingAgentActions] = useState(3); // 模拟有3个待处理项
    const [notificationApi, contextHolder] = notification.useNotification();

    // 模拟定期检查新的Agent操作
    useEffect(() => {
        // 模拟每分钟检查一次新的AI处理结果
        const checkInterval = setInterval(() => {
            // 模拟偶尔会有新处理结果
            if (Math.random() > 0.8) {
                // 20%的概率有新的处理结果
                const newCount = Math.floor(Math.random() * 2) + 1; // 1-2个新结果
                setPendingAgentActions((prev) => prev + newCount);

                // 显示通知
                notificationApi.info({
                    message: "AI助手提醒",
                    description: `AI助手检测到${newCount}条新的邮件处理结果需要您确认`,
                    icon: <RobotOutlined style={{ color: "#1890ff" }} />,
                    placement: "topRight",
                    duration: 5,
                    btn: (
                        <Button type="primary" size="small" onClick={() => setAgentPanelVisible(true)}>
                            立即查看
                        </Button>
                    ),
                });
            }
        }, 60000); // 60秒检查一次

        return () => clearInterval(checkInterval);
    }, []);

    // 事件处理函数
    const handleShowDetails = (flight) => {
        setSelectedFlight(flight);
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setSelectedFlight(null);
    };

    const handleAgentPanelClose = () => {
        setAgentPanelVisible(false);
        // 模拟处理后清空待处理数
        setPendingAgentActions(0);
    };

    return (
        <>
            {contextHolder}
            <Card
                title="航班监控 (DEMO)"
                extra={
                    <Badge count={pendingAgentActions} offset={[-5, 0]}>
                        <Button type="primary" ghost icon={<RobotOutlined />} onClick={() => setAgentPanelVisible(true)}>
                            AI助手待确认
                        </Button>
                    </Badge>
                }
            >
                <Alert message="演示页面" description="此页面用于展示航班监控模块的不同视图（列表、日历、时间轴）以及航班事务处理流程。所有数据均为 Mock 数据，交互仅为前端模拟，不涉及后端。" type="info" showIcon closable style={{ marginBottom: 16 }} />

                {pendingAgentActions > 0 && (
                    <Alert
                        message={
                            <span>
                                AI助手已处理 <Badge count={pendingAgentActions} style={{ backgroundColor: "#1890ff" }} /> 条与航班相关的邮件，需要您的确认
                            </span>
                        }
                        type="info"
                        showIcon
                        icon={<RobotOutlined />}
                        action={
                            <Button size="small" type="primary" onClick={() => setAgentPanelVisible(true)}>
                                查看详情
                            </Button>
                        }
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Tabs activeKey={activeView} onChange={setActiveView}>
                    <TabPane tab="列表视图" key="list">
                        <FlightListView flights={flights} onShowDetails={handleShowDetails} />
                    </TabPane>
                    <TabPane tab="日历视图" key="calendar">
                        <FlightCalendarView flights={flights} onShowDetails={handleShowDetails} />
                    </TabPane>
                    <TabPane tab="时间轴视图" key="timeline">
                        <FlightTimelineView flights={flights} onShowDetails={handleShowDetails} />
                    </TabPane>
                </Tabs>

                {/* 航班事务处理弹窗 */}
                <TransactionModal visible={isModalVisible} flight={selectedFlight} onClose={handleCloseModal} />

                {/* AI助手面板抽屉 */}
                <Drawer
                    title={
                        <span>
                            <RobotOutlined /> AI助手待确认事项
                            {pendingAgentActions > 0 && <Badge count={pendingAgentActions} style={{ marginLeft: 8, backgroundColor: "#1890ff" }} />}
                        </span>
                    }
                    width={900}
                    placement="right"
                    onClose={handleAgentPanelClose}
                    open={agentPanelVisible}
                >
                    <AgentActionsPanel />
                </Drawer>
            </Card>
        </>
    );
};

export default FlightMonitorDemoPage;
